import { CandidateRejectionDialog } from "../../../common/components/functional/candidateRejection/CandidateRejectionDialog";
import { Box, Typography } from "@mui/material";
import { Fragment, useCallback, useEffect, useState } from "react";
import { isFulfilled } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { ActionButton } from "../../../common/components/functional/actionBanner/styled";
import RejectedMessageDialog from "../../../common/components/functional/rejectedMessageDialog";
import { TalentActionBannerContainer } from "../../orders/editOrder/long/styles";
import { ApplicantStatusAction } from "../constants";
import {
  postApproveJobAdApplicants,
  postSendJobAdApplicantRejectedMessage,
  selectAllJobAdApplicants,
} from "./jobAdApplicantsSlice";
import { fetchTalentRejectedDefaultMessageTemplate } from "../../settings/messages/messagesSlice";
import { FLOATING_CHAT_BUTTON_BOTTOM_RAISED } from "../../messaging/messagingSlice";
import { useFloatingChatButtonBottom } from "../../messaging/useFloatingChatButtonBottom";

const DEFAULT_ACTIONS = [
  ApplicantStatusAction.Suitable,
  ApplicantStatusAction.NotSuitable,
  ApplicantStatusAction.SendRejectedMessage,
];

interface Props {
  selectedApplicantIds: string[];
  jobAdId: string | undefined;
}

const ApplicantsActionBanner = ({ selectedApplicantIds, jobAdId }: Props) => {
  const { t } = useTranslation(["jobAds", "common"]);
  const [isRejectedMessageDialogOpen, setIsRejectedMessageDialogOpen] =
    useState(false);
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  useEffect(() => {
    setRejectionDialogOpen(false);
  }, [selectedApplicantIds]);
  const actions: ApplicantStatusAction[] = DEFAULT_ACTIONS;

  const dispatch = useAppDispatch();
  const applicants = useAppSelector(selectAllJobAdApplicants);
  const selectedApplicants = applicants.filter((applicant) =>
    selectedApplicantIds.includes(applicant.id),
  );

  const dispatchApproval = useCallback(
    (isApproved: boolean) => {
      if (jobAdId) {
        dispatch(
          postApproveJobAdApplicants({
            jobAdId: jobAdId,
            applicantIds: selectedApplicants.map((applicant) => applicant.id),
            approve: isApproved,
          }),
        );
      }
    },
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, selectedApplicants, jobAdId],
  );
  const accept = () => {
    dispatchApproval(true);
  };
  const reject = () => {
    setRejectionDialogOpen(true);
  };

  const getRejectableApplicantIds = () =>
    selectedApplicants
      .filter(
        (applicant) =>
          applicant.eezyAccepted === false && !applicant.rejectionMessageSentAt,
      )
      .map((applicant) => applicant.id);

  const sendRejectedMessage = async (message: string) => {
    if (!jobAdId) {
      return;
    }
    const applicantIds = getRejectableApplicantIds();
    if (applicantIds.length === 0) {
      return;
    }
    const action = await dispatch(
      postSendJobAdApplicantRejectedMessage({
        jobAdId,
        applicantIds,
        message,
      }),
    );
    if (isFulfilled(action)) {
      setIsRejectedMessageDialogOpen(false);
    }
  };

  const fetchTemplateMessage = useCallback(async () => {
    if (!jobAdId) {
      return "";
    }
    const action = await dispatch(
      fetchTalentRejectedDefaultMessageTemplate({ jobAdId }),
    );
    return isFulfilled(action) ? action.payload : "";
  }, [dispatch, jobAdId]);

  const getButtonProps = (action: ApplicantStatusAction) => {
    const buttonProps = {
      [ApplicantStatusAction.Suitable]: {
        color: "success" as "success",
        onClick: () => accept(),
      },
      [ApplicantStatusAction.NotSuitable]: {
        color: "error" as "error",
        onClick: () => reject(),
      },
      [ApplicantStatusAction.SendRejectedMessage]: {
        color: "primary" as "primary",
        onClick: () => {
          setIsRejectedMessageDialogOpen(true);
        },
      },
    };
    return buttonProps[action];
  };

  const visibleActions = actions.filter((action) => {
    const selectedStateKeys = new Set(
      selectedApplicants.map((applicant) => {
        if (applicant.rejectionMessageSentAt) {
          return "finalRejected";
        }
        if (applicant.eezyAccepted === true) {
          return "accepted";
        }
        if (applicant.eezyAccepted === false) {
          return "rejected";
        }
        return "new";
      }),
    );

    // Mixed selection state -> no actions, because action target would be ambiguous.
    if (selectedStateKeys.size > 1) {
      return false;
    }

    const allSelectedAccepted =
      selectedApplicants.length > 0 &&
      selectedApplicants.every((applicant) => applicant.eezyAccepted === true);
    const allSelectedRejected =
      selectedApplicants.length > 0 &&
      selectedApplicants.every((applicant) => applicant.eezyAccepted === false);

    if (
      selectedApplicants.some((applicant) => !!applicant.rejectionMessageSentAt)
    ) {
      return false;
    }

    if (action === ApplicantStatusAction.Suitable && allSelectedAccepted) {
      return false;
    }

    if (action === ApplicantStatusAction.NotSuitable && allSelectedRejected) {
      return false;
    }

    if (action !== ApplicantStatusAction.SendRejectedMessage) {
      return true;
    }
    return (
      selectedApplicants.length > 0 &&
      selectedApplicants.every(
        (applicant) => applicant.eezyAccepted === false,
      ) &&
      selectedApplicants.some((applicant) => !applicant.rejectionMessageSentAt)
    );
  });

  useFloatingChatButtonBottom(
    visibleActions.length > 0 ? FLOATING_CHAT_BUTTON_BOTTOM_RAISED : null,
  );

  return (
    <>
      {visibleActions.length > 0 && (
        <TalentActionBannerContainer>
          {visibleActions.map((action, index) => (
            <Fragment key={action}>
              <ActionButton
                key={action}
                variant="text"
                {...getButtonProps(action)}
              >
                <Typography variant="header" fontSize="16px" fontWeight={700}>
                  {t(action)}
                </Typography>
              </ActionButton>
              <Box
                hidden={index === visibleActions.length - 1}
                width="2px"
                height="24px"
                bgcolor="#000A441C"
              />
            </Fragment>
          ))}
        </TalentActionBannerContainer>
      )}
      {selectedApplicants.length > 0 && (
        <CandidateRejectionDialog
          key={selectedApplicants.map((applicant) => applicant.id).join(",")}
          open={rejectionDialogOpen}
          talentName={selectedApplicants
            .map((applicant) => `${applicant.firstName} ${applicant.lastName}`)
            .join("\n")}
          candidateCount={selectedApplicants.length}
          onClose={() => setRejectionDialogOpen(false)}
          onReject={async (details) => {
            if (!jobAdId) return false;
            return isFulfilled(
              await dispatch(
                postApproveJobAdApplicants({
                  jobAdId,
                  applicantIds: selectedApplicants.map(
                    (applicant) => applicant.id,
                  ),
                  approve: false,
                  ...details,
                }),
              ),
            );
          }}
        />
      )}
      <RejectedMessageDialog
        open={isRejectedMessageDialogOpen}
        title={t("sendRejectedMessage")}
        onClose={() => setIsRejectedMessageDialogOpen(false)}
        onSend={(message) => {
          void sendRejectedMessage(message);
        }}
        fetchTemplateMessage={fetchTemplateMessage}
      />
    </>
  );
};

export default ApplicantsActionBanner;
